/**
 * AppCryptoEngine — production implementation using WebCrypto.
 *
 * SHA-256 for application-level manifest hashing.
 * crypto.getRandomValues for CSPRNG IDs.
 *
 * We do NOT compute piece hashes — BitTorrent engines own that.
 * We do NOT use SHA-1 at application level — SHA-256 is the standard.
 * We do NOT implement encrypt/decrypt — the old btoa approach was fake security.
 *
 * No external dependencies: WebCrypto is available in all modern browsers
 * and in React Native's JSC/Hermes runtime.
 */

import { CryptoEngine } from '@share/domain';

export class AppCryptoEngine implements CryptoEngine {
  /**
   * SHA-256 hash for application manifest integrity.
   * Returns 64-char lowercase hex string.
   */
  async hashManifest(data: Uint8Array): Promise<string> {
    // Create a fresh ArrayBuffer copy to avoid SharedArrayBuffer type issues
    const buf = new ArrayBuffer(data.byteLength);
    new Uint8Array(buf).set(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * CSPRNG-based random ID generation.
   * Returns hex string of length byteLength * 2.
   * Default: 16 bytes = 32 hex chars.
   */
  generateId(byteLength: number = 16): string {
    const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}

/** Singleton instance */
export const CryptoEngineImpl = new AppCryptoEngine();
